import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { UserManager, User } from 'oidc-client-ts';
import { getOidcConfig } from './authConfig';
import { configureApi } from '../api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (returnUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

let userManagerInstance: UserManager | null = null;

/** Lazy singleton — created after runtime config is loaded in main.tsx. */
export function getUserManager(): UserManager {
  userManagerInstance ??= new UserManager(getOidcConfig());
  return userManagerInstance;
}

/** @deprecated Prefer getUserManager(); kept for SilentRenewPage compatibility. */
export const userManager = {
  signinSilentCallback: (...args: Parameters<UserManager['signinSilentCallback']>) =>
    getUserManager().signinSilentCallback(...args),
} as Pick<UserManager, 'signinSilentCallback'>;

let signinRedirectCompletion: Promise<User> | null = null;

/** Single-flight OIDC code exchange so StrictMode double-mount performs one token POST. */
export function completeSigninRedirect(): Promise<User> {
  signinRedirectCompletion ??= getUserManager().signinRedirectCallback();
  return signinRedirectCompletion;
}

/** Test-only: clear the memoized callback promise between vitest cases. */
export function resetSigninRedirectCompletionForTests(): void {
  signinRedirectCompletion = null;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Re-bind when `user` changes. Do not write a ref during render (react-hooks/refs).
  useEffect(() => {
    const manager = getUserManager();
    configureApi(
      () => user?.access_token ?? null,
      () => {
        void manager.removeUser();
      }
    );
  }, [user]);

  useEffect(() => {
    const manager = getUserManager();

    // Check if user is already logged in
    const loadUser = async () => {
      try {
        const currentUser = await manager.getUser();
        if (currentUser && !currentUser.expired) {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Failed to load user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();

    // Handle user loaded/unloaded events
    const handleUserLoaded = (loadedUser: User) => {
      setUser(loadedUser);
    };

    const handleUserUnloaded = () => {
      setUser(null);
    };

    const handleAccessTokenExpired = () => {
      console.warn('Access token expired; clearing session.');
      void manager.removeUser();
    };

    const handleSilentRenewError = (error: Error) => {
      console.error('Silent renew error:', error);
    };

    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addUserUnloaded(handleUserUnloaded);
    // automaticSilentRenew already registers SilentRenewService on accessTokenExpiring —
    // do not add a second signinSilent listener (refresh-token rotation would invalidate the loser).
    manager.events.addAccessTokenExpired(handleAccessTokenExpired);
    manager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeUserUnloaded(handleUserUnloaded);
      manager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  const login = useCallback(async (returnUrl?: string) => {
    await getUserManager().signinRedirect({ state: { returnUrl } });
  }, []);

  const logout = useCallback(async () => {
    await getUserManager().signoutRedirect();
  }, []);

  const getAccessToken = useCallback(() => {
    return user?.access_token ?? null;
  }, [user]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user && !user.expired,
      isLoading,
      login,
      logout,
      getAccessToken,
    }),
    [user, isLoading, login, logout, getAccessToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
